import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Button,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Divider,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  CircularProgress,
  Card,
  CardContent,
  useTheme,
  AppBar,
  Toolbar,
  IconButton
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Restaurant as RestaurantIcon,
  ShoppingCart as CartIcon,
  CreditCard as CreditCardIcon,
  Person as PersonIcon,
  Receipt as ReceiptIcon,
  LocalDining as DiningIcon,
  Store as StoreIcon,
  Home as HomeIcon
} from '@mui/icons-material';
import OrderService from '../../firebase/OrderService';

const steps = ['Datos personales', 'Resumen del pedido', 'Método de pago'];

const RestaurantCheckout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();

  // Estados
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [orderType, setOrderType] = useState('');
  const [cart, setCart] = useState([]);
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    notes: ''
  });
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [tableNumber, setTableNumber] = useState('');

  // Cálculos
  const subtotal = cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  const iva = subtotal * 0.10;
  const total = subtotal + iva;
  // Efecto para cargar datos iniciales
  useEffect(() => {
    // Intentar obtener datos del query string
    const queryParams = new URLSearchParams(location.search);
    const type = queryParams.get('type');
    
    if (type && ['local', 'takeaway', 'delivery'].includes(type)) {
      setOrderType(type);
      
      // Cargar carrito del localStorage
      const savedCart = localStorage.getItem(`cart_${type}`);
      if (savedCart) {
        try {
          const parsedCart = JSON.parse(savedCart);
          if (parsedCart.length > 0) {
            setCart(parsedCart);
          } else {
            // Si el carrito está vacío, redirigir
            navigate(`/pedido-${type}`);
          }
        } catch (error) {
          console.error('Error parsing saved cart:', error);
          setError('Error al cargar el carrito. Por favor, vuelve a intentarlo.');
        }
      } else {
        // Si no hay carrito guardado, redirigir
        navigate(`/pedido-${type}`);
      }
      
      // Cargar información del cliente del localStorage
      const savedCustomerInfo = localStorage.getItem('customerInfo');
      if (savedCustomerInfo) {
        try {
          setCustomerInfo(JSON.parse(savedCustomerInfo));
        } catch (error) {
          console.error('Error parsing saved customer info:', error);
        }
      }
    } else {
      // Si no hay tipo de pedido, redirigir a la página principal
      navigate('/');
    }
  }, [location, navigate]);

  // Manejar cambios en formulario de cliente
  const handleCustomerInfoChange = (e) => {
    const { name, value } = e.target;
    const updatedInfo = {
      ...customerInfo,
      [name]: value
    };
    
    setCustomerInfo(updatedInfo);
    localStorage.setItem('customerInfo', JSON.stringify(updatedInfo));
  };

  // Avanzar al siguiente paso
  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  // Volver al paso anterior
  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  // Procesar pedido
  const handleSubmit = async () => {
    setLoading(true);
    
    try {
      // Construir objeto de pedido
      const orderData = {
        type: orderType,
        status: 'pending',
        customer: {
          name: customerInfo.name,
          email: customerInfo.email,
          phone: customerInfo.phone,
          address: orderType === 'delivery' ? customerInfo.address : ''
        },
        items: cart.map(item => ({
          id: item.id,
          name: item.name,
          price: item.price,
          quantity: item.quantity
        })),
        total: total,
        table: orderType === 'local' ? tableNumber : '',
        date: new Date().toISOString().split('T')[0],
        time: new Date().toTimeString().split(' ')[0].substring(0, 5),
        notes: customerInfo.notes,
        paymentMethod: paymentMethod,
        paymentStatus: 'pending'
      };
      
      // Guardar pedido en Firebase
      await OrderService.createOrder(orderData);
      
      // Limpiar localStorage
      localStorage.removeItem(`cart_${orderType}`);
      
      // Mostrar mensaje de éxito
      setOrderSuccess(true);
      
    } catch (err) {
      console.error('Error submitting order:', err);
      setError('Error al procesar el pedido. Por favor, inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  };
  // Renderizar el contenido del paso actual
  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <PersonIcon sx={{ mr: 1 }} /> Datos personales
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  name="name"
                  label="Nombre completo"
                  value={customerInfo.name}
                  onChange={handleCustomerInfoChange}
                  fullWidth
                  required
                  error={!customerInfo.name}
                  helperText={!customerInfo.name ? "Campo obligatorio" : ""}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="email"
                  label="Email"
                  type="email"
                  value={customerInfo.email}
                  onChange={handleCustomerInfoChange}
                  fullWidth
                  required
                  error={!customerInfo.email}
                  helperText={!customerInfo.email ? "Campo obligatorio" : ""}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="phone"
                  label="Teléfono"
                  value={customerInfo.phone}
                  onChange={handleCustomerInfoChange}
                  fullWidth
                  required
                  error={!customerInfo.phone}
                  helperText={!customerInfo.phone ? "Campo obligatorio" : ""}
                />
              </Grid>
              {orderType === 'local' && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Número de mesa"
                    value={tableNumber}
                    onChange={(e) => setTableNumber(e.target.value)}
                    fullWidth
                    required
                    error={!tableNumber}
                    helperText={!tableNumber ? "Por favor, introduce el número de mesa" : ""}
                  />
                </Grid>
              )}
              {orderType === 'delivery' && (
                <Grid item xs={12}>
                  <TextField
                    name="address"
                    label="Dirección de entrega"
                    value={customerInfo.address}
                    onChange={handleCustomerInfoChange}
                    fullWidth
                    required
                    multiline
                    rows={3}
                    error={!customerInfo.address}
                    helperText={!customerInfo.address ? "Campo obligatorio para entrega a domicilio" : ""}
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <TextField
                  name="notes"
                  label="Notas adicionales"
                  value={customerInfo.notes}
                  onChange={handleCustomerInfoChange}
                  fullWidth
                  multiline
                  rows={2}
                  placeholder="Alergias, preferencias, instrucciones especiales, etc."
                />
              </Grid>
            </Grid>
          </Box>
        );
      case 1:
        return (
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <ReceiptIcon sx={{ mr: 1 }} /> Resumen del pedido
            </Typography>
            <Paper variant="outlined" sx={{ mb: 3 }}>
              <List sx={{ width: '100%' }}>
                {cart.map((item) => (
                  <ListItem key={item.id}>
                    <ListItemText 
                      primary={item.name} 
                      secondary={`${item.quantity} x ${item.price.toFixed(2)}€`} 
                    />
                    <ListItemSecondaryAction>
                      <Typography variant="body2">
                        {(item.price * item.quantity).toFixed(2)}€
                      </Typography>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </Paper>
            
            <Box sx={{ bgcolor: theme.palette.neutral.main, p: 2, borderRadius: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body1">Subtotal</Typography>
                <Typography variant="body1">{subtotal.toFixed(2)}€</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body1">IVA (10%)</Typography>
                <Typography variant="body1">{iva.toFixed(2)}€</Typography>
              </Box>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="subtitle1" fontWeight="bold">TOTAL</Typography>
                <Typography variant="subtitle1" fontWeight="bold">{total.toFixed(2)}€</Typography>
              </Box>
            </Box>
            
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" gutterBottom>Datos de contacto:</Typography>
              <Typography variant="body2">Nombre: {customerInfo.name}</Typography>
              <Typography variant="body2">Email: {customerInfo.email}</Typography>
              <Typography variant="body2">Teléfono: {customerInfo.phone}</Typography>
              {orderType === 'delivery' && (
                <Typography variant="body2">Dirección: {customerInfo.address}</Typography>
              )}
              {orderType === 'local' && (
                <Typography variant="body2">Mesa: {tableNumber}</Typography>
              )}
              {customerInfo.notes && (
                <Typography variant="body2">Notas: {customerInfo.notes}</Typography>
              )}
            </Box>
          </Box>
        );
      case 2:
        return (
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <CreditCardIcon sx={{ mr: 1 }} /> Método de pago
            </Typography>
            <FormControl component="fieldset" sx={{ width: '100%', mb: 3 }}>
              <RadioGroup
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
              >
                <Paper 
                  variant="outlined" 
                  sx={{ 
                    mb: 2, 
                    p: 2, 
                    border: paymentMethod === 'card' ? `2px solid ${theme.palette.primary.main}` : undefined 
                  }}
                >
                  <FormControlLabel 
                    value="card" 
                    control={<Radio color="primary" />} 
                    label={
                      <Box>
                        <Typography variant="subtitle1">Tarjeta de crédito/débito</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Pago seguro mediante pasarela de pago
                        </Typography>
                      </Box>
                    }
                    sx={{ width: '100%' }}
                  />
                </Paper>
                
                <Paper 
                  variant="outlined" 
                  sx={{ 
                    mb: 2, 
                    p: 2, 
                    border: paymentMethod === 'paypal' ? `2px solid ${theme.palette.primary.main}` : undefined 
                  }}
                >
                  <FormControlLabel 
                    value="paypal" 
                    control={<Radio color="primary" />} 
                    label={
                      <Box>
                        <Typography variant="subtitle1">PayPal</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Pago rápido y seguro con PayPal
                        </Typography>
                      </Box>
                    }
                    sx={{ width: '100%' }}
                  />
                </Paper>
                
                {(orderType === 'local' || orderType === 'takeaway') && (
                  <Paper 
                    variant="outlined" 
                    sx={{ 
                      p: 2, 
                      border: paymentMethod === 'cash' ? `2px solid ${theme.palette.primary.main}` : undefined 
                    }}
                  >
                    <FormControlLabel 
                      value="cash" 
                      control={<Radio color="primary" />} 
                      label={
                        <Box>
                          <Typography variant="subtitle1">Efectivo</Typography>
                          <Typography variant="body2" color="text.secondary">
                            Pago en el momento de {orderType === 'local' ? 'servicio' : 'recogida'}
                          </Typography>
                        </Box>
                      }
                      sx={{ width: '100%' }}
                    />
                  </Paper>
                )}
              </RadioGroup>
            </FormControl>
            
            <Box sx={{ bgcolor: theme.palette.neutral.main, p: 2, borderRadius: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Resumen del pedido
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Productos ({cart.length})</Typography>
                <Typography variant="body2">{subtotal.toFixed(2)}€</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">IVA (10%)</Typography>
                <Typography variant="body2">{iva.toFixed(2)}€</Typography>
              </Box>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="subtitle2">Total a pagar</Typography>
                <Typography variant="subtitle2">{total.toFixed(2)}€</Typography>
              </Box>
            </Box>
          </Box>
        );
      default:
        return 'Paso desconocido';
    }
  };
  // Si está cargando
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Si hay error
  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}>
            Volver
          </Button>
        </Box>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Barra superior */}
      <AppBar 
        position="static" 
        color="default" 
        elevation={0}
        sx={{ 
          bgcolor: theme.palette.background.default, 
          borderBottom: `1px solid ${theme.palette.divider}`,
          mb: 3
        }}
      >
        <Toolbar>
          <IconButton 
            edge="start" 
            color="inherit" 
            onClick={() => navigate(`/pedido-${orderType}`)}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <RestaurantIcon sx={{ mr: 2, color: theme.palette.primary.main }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontFamily: theme.typography.h6.fontFamily }}>
            Finalizar Pedido
          </Typography>
          <CartIcon sx={{ color: theme.palette.primary.main }} />
          <Typography variant="body1" sx={{ ml: 1 }}>
            {cart.length} items
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Si el pedido se ha completado con éxito */}
      {orderSuccess ? (
        <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h5" color="primary" gutterBottom>
              ¡Pedido realizado con éxito!
            </Typography>
            <Alert severity="success" sx={{ mx: 'auto', maxWidth: 500, mb: 3 }}>
              Tu pedido ha sido confirmado y está siendo procesado.
            </Alert>
            
            <Typography variant="body1">
              Hemos enviado los detalles a <strong>{customerInfo.email}</strong>.
            </Typography>
            <Typography variant="body1" gutterBottom>
              {orderType === 'delivery' 
                ? 'Te avisaremos cuando el repartidor esté en camino.' 
                : orderType === 'takeaway'
                  ? 'Te avisaremos cuando tu pedido esté listo para recoger.'
                  : `Tu pedido será servido en la mesa ${tableNumber} en breve.`}
            </Typography>
          </Box>
          
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate('/')}
            sx={{ 
              mt: 2,
              bgcolor: theme.palette.primary.main,
              '&:hover': {
                bgcolor: theme.palette.primary.dark
              }
            }}
          >
            Volver al Inicio
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {/* Pasos del proceso */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3, borderRadius: 3, mb: 3 }}>
              <Stepper activeStep={activeStep} alternativeLabel>
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Paper>
          </Grid>
          
          {/* Contenido principal */}
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              {getStepContent(activeStep)}
            </Paper>
            
            {/* Botones de navegación */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
              <Button
                disabled={activeStep === 0}
                onClick={handleBack}
                variant="outlined"
              >
                Atrás
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={activeStep === steps.length - 1 ? handleSubmit : handleNext}
                disabled={
                  (activeStep === 0 && (
                    !customerInfo.name || 
                    !customerInfo.email || 
                    !customerInfo.phone || 
                    (orderType === 'delivery' && !customerInfo.address) ||
                    (orderType === 'local' && !tableNumber)
                  )) ||
                  loading
                }
                sx={{
                  bgcolor: theme.palette.primary.main,
                  '&:hover': {
                    bgcolor: theme.palette.primary.dark
                  }
                }}
              >
                {activeStep === steps.length - 1 ? 'Confirmar Pedido' : 'Siguiente'}
              </Button>
            </Box>
          </Grid>
          
          {/* Resumen lateral */}
          <Grid item xs={12} md={4}>
            <Card sx={{ borderRadius: 3, mb: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Resumen del Pedido
                </Typography>
                <List dense>
                  {cart.map((item) => (
                    <ListItem key={item.id} disableGutters>
                      <ListItemText
                        primary={`${item.quantity}x ${item.name}`}
                      />
                      <Typography variant="body2">
                        {(item.price * item.quantity).toFixed(2)}€
                      </Typography>
                    </ListItem>
                  ))}
                </List>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Subtotal</Typography>
                  <Typography variant="body2">{subtotal.toFixed(2)}€</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">IVA (10%)</Typography>
                  <Typography variant="body2">{iva.toFixed(2)}€</Typography>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="subtitle1" fontWeight="bold">TOTAL</Typography>
                  <Typography variant="subtitle1" fontWeight="bold" color="primary">
                    {total.toFixed(2)}€
                  </Typography>
                </Box>
              </CardContent>
            </Card>
            
            <Card sx={{ borderRadius: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Tipo de Pedido
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  {orderType === 'local' && <DiningIcon color="primary" sx={{ mr: 1 }} />}
                  {orderType === 'takeaway' && <StoreIcon color="primary" sx={{ mr: 1 }} />}
                  {orderType === 'delivery' && <HomeIcon color="primary" sx={{ mr: 1 }} />}
                  <Typography>
                    {orderType === 'local' ? 'En Mesa' : 
                     orderType === 'takeaway' ? 'Para Recoger' : 'A Domicilio'}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Container>
  );
};

export default RestaurantCheckout;